import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { play, soundTypes } from '../../util/sound';

export const lobbyMessageEnum = {
  MESSAGE: 0,
  ROLLED: 1,
  RESTART_IN_PROGRESS: 2,
  INVITE: 3,
  INVITED: 4,
  ALREADY_INVITED: 5,
};

export const gameLobbyMessageEnum = {
  MESSAGE: 0,
  PLAYER_JOIN: 1,
  PLAYER_LEAVE: 2,
};

const addPlayerToTable = (state, p) => {
  if (p.table === null) {
    return;
  }

  const table = state.tables[p.table] ?? null;
  if (table === null) {
    return;
  }
  const players = table.players;
  if (players.includes(state.localPlayerID) && !table.started) {
    play(soundTypes.JOIN);
  }

  players.push(p.id);

  const tableMessages = state.tableMessages[p.table] ?? null;
  if (tableMessages !== null) {
    tableMessages.push({type: gameLobbyMessageEnum.PLAYER_JOIN, args: {name: p.name}});
  }
};

const removePlayerFromTable = (state, p) => {
  if (p.table === null) {
    return;
  }

  const table = state.tables[p.table] ?? null;
  if (table === null) {
    return;
  }

  const players = state.tables[p.table].players;
  const i = players.indexOf(p.id);
  players.splice(i, 1);

  const tableMessages = state.tableMessages[p.table] ?? null;
  if (tableMessages !== null) {
    tableMessages.push({type: gameLobbyMessageEnum.PLAYER_LEAVE, args: {name: p.name}});
  }
};

export const roomSlice = createSlice({
  name: 'room',
  initialState: {
    id: null,
    players: {},
    tables: {},
    tableMessages: {},
    messages: [],
    loaded: false,
    connectionArgs: null,
    localPlayerID: null,
  },
  reducers: {
    reset: (state, action) => {
      state.players = {};
      state.tables = {};
      state.tableMessages = {};
      state.messages = [];
      state.loaded = false;
      state.localPlayerID = null;
    },

    // Players
    addPlayer: (state, action) => {
      let player = action.payload;
      state.players[player.id] = player;

      if (player.local) {
        state.localPlayerID = player.id;
      }

      addPlayerToTable(state, player);
    },
    removePlayer: (state, action) => {
      const player = state.players[action.payload.id];
      removePlayerFromTable(state, player);
      delete state.players[action.payload.id];
    },
    updatePlayer: (state, action) => {
      let p = action.payload;

      // If the user is changing table we need to update tables also
      let oldPlayer = state.players[p.id];
      let newPlayer = { ...state.players[p.id], ...p };
      if (oldPlayer.table !== newPlayer.table) {
        removePlayerFromTable(state, oldPlayer);
        addPlayerToTable(state, newPlayer);
      }

      state.players[p.id] = newPlayer;
    },

    // Tables
    addTable: (state, action) => {
      const table = action.payload;
      state.tables[table.id] = table;
      state.tableMessages[table.id] = [];
    },
    removeTable: (state, action) => {
      delete state.tables[action.payload.id];
      delete state.tableMessages[action.payload.id];
    },
    updateTable: (state, action) => {
      let table = action.payload;
      state.tables[table.id] = { ...state.tables[table.id], ...table };
    },

    // Chat
    addMessage: (state, action) => {
      const payload = action.payload;
      const player = state.players[payload.playerID] ?? null;
      if (player === null) {
        // If we can't find the player just ignore the message
        return;
      }

      if (payload.tableID !== undefined) {
        const tableMessages = state.tableMessages[payload.tableID] ?? null;
        if (tableMessages === null) {
          return;
        }

        tableMessages.push({type: gameLobbyMessageEnum.MESSAGE, args: {name: player.name, data: payload.data}});
      } else {
        state.messages.push({type: lobbyMessageEnum.MESSAGE, args: {name: player.name, data: payload.data}});
      }
    },
    addRoll: (state, action) => {
      const payload = action.payload;
      const player = state.players[payload.playerID] ?? null;
      if (player === null) {
        // If we can't find the player just ignore the message
        return;
      }

      state.messages.push({type: lobbyMessageEnum.ROLLED, args: {name: player.name, points: payload.points, word: payload.word}});
    },
    addRestartInProgress: (state, action) => {
      state.messages.push({type: lobbyMessageEnum.RESTART_IN_PROGRESS});
    },
    invited: (state, action) => {
      const payload = action.payload;
      const table = state.tables[payload.tableID] ?? null;
      if (table === null) {
        return;
      }
      table.invited = true;

      const player = state.players[payload.playerID] ?? null;
      if (player === null) {
        return;
      }
      state.messages.push({type: lobbyMessageEnum.INVITED, args: {name: player.name}});
      play(soundTypes.INVITE_GAME);
    },
    invite: (state, action) => {
      const payload = action.payload;
      const player = state.players[payload.playerID] ?? null;
      if (player === null) {
        return;
      }
      state.messages.push({type: lobbyMessageEnum.INVITE, args: {name: player.name}});
    },
    alreadyInvited: (state, action) => {
      state.messages.push({type: lobbyMessageEnum.ALREADY_INVITED});
    },

    // Other
    setLoaded: (state, action) => {
      state.loaded = action.payload;
    },

    setConnectionArgs: (state, action) => {
      state.connectionArgs = action.payload;
    },
    setID: (state, action) => {
      state.id = action.payload;
    },
  },
});

export const { addPlayer, removePlayer, updatePlayer, addTable, removeTable, updateTable } = roomSlice.actions;

export const getPlayer = (state, id) => state.room.players[id];
export const getPlayerProperty = (state, id, prop) => {
  const player = state.room.players[id] ?? null;
  if (player === null) {
    return null;
  }
  return player[prop] ?? null;
};

export const getPlayerIDsSorted = createSelector(
  [
    (state) => state.room.players,
    (state) => state.init.connectionArgs.locale,
  ],
  (players, locale) => {
    return Object.values(players).sort((a, b) => {
      if (a.lvl === b.lvl) {
        return a.name.localeCompare(b.name, locale);
      }
      return b.lvl - a.lvl;
    }).map((p) => p.id);
  }
);

export const getTable = (state, id) => state.room.tables[id] ?? null;
export const getTableIDsSorted = (started) => {
  return createSelector(
    [
      (state) => state.room.tables,
      (state) => state.init.connectionArgs.locale,
    ],
    (tables, locale) => {
      return Object.values(tables).filter(t => t.started === started).sort((a, b) => {
        if (a.started === b.started) {
          return a.name.localeCompare(b.name, locale);
        }
        return (a.started ? 1 : 0) - (b.started ? 1 : 0);
      }).map((t) => t.id);
    }
  );
};

export const getLocalPlayer = (state) => state.room.players[state.room.localPlayerID] ?? null;

export const getNumMessages = (state) => state.room.messages.length;
export const getMessage = (state, id) => state.room.messages[id] ?? null;

export const getNumTableMessages = (state, id) => state.room.tableMessages[id].length;
export const getTableMessage = (state, id, idx) => state.room.tableMessages[id][idx] ?? null;

export const getRoomTabState = (state) => {
  if (state.room.loaded) {
    return 2;
  }
  return (state.room.connectionArgs !== null) ? 1 : 0;
}

export const getConnectionArgs = (state) => state.room.connectionArgs;
export const getID = (state) => state.room.id;

export default roomSlice.reducer;
